// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useState } from 'react';
import { useAuthContext } from "@asgardeo/auth-react";
import { AccountDetails } from '../data/utils/types';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { Box, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
    TextField, Typography, Stack 
} from '@mui/material';
import DOMPurify from 'dompurify';
import { Store } from 'react-notifications-component';
import CircularLoading from './Loading';

type EscalateDialogProps = {
    isEscalateDialogOpen: boolean;
    data: AccountDetails;
    setIsEscalateDialogOpen: (isDialogOpen: boolean) => void;
}

export default function EscalateDialog(props: EscalateDialogProps): JSX.Element {

    const [caseId, setCaseId] = useState('');
    const [reason, setReason] = useState('');
    const [severity, setSeverity] = useState('');
    const [source, setSource] = useState('');
    const [justification, setJustification] = useState('');
    const [loading, setLoading] = useState(false);
    const { httpRequest } = useAuthContext();

    const handleClose = () => {
        props.setIsEscalateDialogOpen(false);
    };

    const SelectSeverity = () => {
        const handleChange = (event: SelectChangeEvent) => {
            setSeverity(event.target.value as string);
        };
        return (
            <Box>
                <FormControl required sx={{ minWidth: 120 }}>
                    <InputLabel id="severity-select-label">Severity</InputLabel>
                    <Select
                        labelId="severity-select-label"
                        id="severity-select"
                        value={severity}
                        label="Severity"
                        onChange={handleChange}
                    >
                        <MenuItem value={"High Severity"}>High Severity</MenuItem>
                        <MenuItem value={"Medium Severity"}>Medium Severity</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        )
    };

    const SelectSource = () => {
        const handleChange = (event: SelectChangeEvent) => {
            setSource(event.target.value as string);
        };
        return (
            <Box>
                <FormControl required sx={{ minWidth: 120 }}>
                    <InputLabel id="source-select-label">Source</InputLabel>
                    <Select
                        labelId="source-select-label"
                        id="source-select"
                        value={source}
                        label="Source"
                        onChange={handleChange}
                    >
                        <MenuItem value={"Customer"}>Customer</MenuItem>
                        <MenuItem value={"Internal"}>Internal</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        )
    };

    const SelectReason = () => {
        const handleChange = (event: SelectChangeEvent) => {
            setReason(event.target.value as string);
        };
        return (
            <Box>
                <FormControl required sx={{ minWidth: 120 }}>
                    <InputLabel id="reason-select-label">Reason</InputLabel>
                    <Select
                        labelId="reason-select-label"
                        id="reason-select"
                        value={reason}
                        label="Reason"
                        onChange={handleChange}
                    >
                        <MenuItem value={"Inactivity"}>Inactivity</MenuItem>
                        <MenuItem value={"Lack Of Progress"}>Lack Of Progress</MenuItem>
                        <MenuItem value={"Customer Imposed Deadline"}>Customer Imposed Deadline</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        )
    };

    const addEscalation = async () => {
        if (caseId === '' || reason === '' || severity === '' || source === '' || justification === '') {
            Store.addNotification({
                title: "error!",
                message: "Please fill all the required fields.",
                type: "warning",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: {
                    duration: 5000,
                    onScreen: true
                }
            });
            return;
        }
        const escalateURL = RESOURCE_SERVER_URLS[0] + `/accounts/${props.data.number}/cases/${caseId}/escalate`;
        const sanitizedJustification = DOMPurify.sanitize(justification);
        setLoading(true);
        try {
            const response = await httpRequest({
                url: escalateURL,
                method: 'POST',
                headers: { "accept": "application/json" },
                data: {
                    "justification": sanitizedJustification, "requestSource": source,
                    "reason": reason, "severity": severity
                }
            });
            if (response.status === 201) {
                Store.addNotification({
                    title: "success!",
                    message: "Escalation added successfully.",
                    type: "success",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                });
                setLoading(false);
                handleClose();
            }
        } catch (error: any) {
            console.error(error.response.data);
            Store.addNotification({
                title: "failed!",
                message: "Escalation submission unsuccessful.",
                type: "danger",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: {
                    duration: 5000,
                    onScreen: true
                }
            });
            setLoading(false);
            handleClose();
        }
    };

    return (
        <>
            <CircularLoading openLoading={loading} />
            <Dialog open={props.isEscalateDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>Escalation Submission</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Stack spacing={2}>
                            <Typography variant="h6">
                                Account: {props.data.name}
                            </Typography>
                            <SelectSource />
                            <SelectSeverity />
                            <SelectReason />
                            <Box>
                                <TextField
                                    required
                                    id="case-id"
                                    label="Case Id"
                                    value={caseId}
                                    onChange={(e) => setCaseId(e.target.value)}
                                />
                            </Box>
                            <Box>
                                <TextField
                                    fullWidth
                                    required
                                    id="justification-id"
                                    label="Justification"
                                    placeholder={justification}
                                    multiline
                                    rows={6}
                                    onChange={(e) => setJustification(e.target.value)}
                                />
                            </Box>
                        </Stack>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={addEscalation}>Escalate</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
